import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
    Sparkles, 
    Wand2, 
    Brain, 
    BookOpen, 
    RefreshCw, 
    CheckCircle, 
    Clock, 
    Loader2,
    Zap,
    Globe,
    Atom,
    Rocket,
    GraduationCap,
    FileText,
    Presentation,
    ClipboardCheck,
    Users,
    Play,
    ChevronRight,
    Infinity,
    MessageSquare
} from 'lucide-react';

const exampleTopics = [
    { icon: '📈', name: 'Stock Trading Fundamentals', category: 'Finance' },
    { icon: '🧠', name: 'Machine Learning Basics', category: 'Technology' },
    { icon: '⚛️', name: 'Quantum Computing', category: 'Science' },
    { icon: '👥', name: 'HR Management Advanced', category: 'Business' },
    { icon: '📊', name: 'Advanced Excel & Data Analysis', category: 'Tools' },
    { icon: '🚀', name: 'Product Management', category: 'Business' },
    { icon: '🌍', name: 'Geopolitics & Global Economics', category: 'Knowledge' },
    { icon: '🔮', name: 'Future Technologies', category: 'Innovation' },
];

const ModuleGenerator = () => {
    const { api } = useAuth();
    const [activeTab, setActiveTab] = useState('quick');
    const [loading, setLoading] = useState(false);
    const [generations, setGenerations] = useState([]);
    const [currentGeneration, setCurrentGeneration] = useState(null);
    const [pollingInterval, setPollingInterval] = useState(null);
    
    // Quick generate
    const [quickPrompt, setQuickPrompt] = useState('');
    
    // Advanced generate
    const [advancedForm, setAdvancedForm] = useState({
        topic: '',
        description: '',
        difficulty: 'intermediate',
        target_audience: 'professionals',
        num_lessons: 5,
        include_assessments: true,
        include_simulations: true,
        include_visuals: true,
        language: 'English'
    });

    useEffect(() => {
        fetchGenerations();
    }, []);

    useEffect(() => {
        return () => {
            if (pollingInterval) clearInterval(pollingInterval);
        };
    }, [pollingInterval]);

    const fetchGenerations = async () => {
        try {
            const response = await api.get('/ai-generator/generations');
            setGenerations(response.data);
        } catch (error) {
            console.error('Failed to fetch generations:', error);
        }
    };

    const pollGenerationStatus = (generationId) => {
        const interval = setInterval(async () => {
            try {
                const response = await api.get(`/ai-generator/generation/${generationId}`);
                setCurrentGeneration(response.data);
                
                if (response.data.status === 'completed' || response.data.status === 'failed') {
                    clearInterval(interval);
                    setPollingInterval(null);
                    fetchGenerations();
                    
                    if (response.data.status === 'completed') {
                        toast.success('Module Generated!', {
                            description: 'Your training module is ready for review'
                        });
                    } else {
                        toast.error('Generation Failed', {
                            description: response.data.status_message
                        });
                    }
                }
            } catch (error) {
                console.error('Polling error:', error);
            }
        }, 3000);
        
        setPollingInterval(interval);
    };

    const handleQuickGenerate = async () => {
        if (!quickPrompt.trim()) {
            toast.error('Please enter a topic or prompt');
            return;
        }

        setLoading(true);
        try {
            const response = await api.post('/ai-generator/quick-generate', {
                prompt: quickPrompt
            });
            
            setCurrentGeneration({ ...response.data, progress: 0 });
            pollGenerationStatus(response.data.generation_id);
            toast.info('Generation Started', {
                description: 'Your AI module is being created...'
            });
        } catch (error) {
            toast.error('Generation failed', {
                description: error.response?.data?.detail || 'An error occurred'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleAdvancedGenerate = async () => {
        if (!advancedForm.topic.trim()) {
            toast.error('Please enter a topic');
            return;
        }

        setLoading(true);
        try {
            const response = await api.post('/ai-generator/generate-module', advancedForm);
            
            setCurrentGeneration({ ...response.data, progress: 0 });
            pollGenerationStatus(response.data.generation_id);
            toast.info('Generation Started', {
                description: 'Creating comprehensive training module...'
            });
        } catch (error) {
            toast.error('Generation failed', {
                description: error.response?.data?.detail || 'An error occurred'
            });
        } finally {
            setLoading(false);
        }
    };

    const selectExampleTopic = (topic) => {
        if (activeTab === 'quick') {
            setQuickPrompt(`Generate complete training for ${topic.name}`);
        } else {
            setAdvancedForm(prev => ({ ...prev, topic: topic.name }));
        }
    };

    return (
        <MainLayout>
            <div className="space-y-6 animate-fade-in" data-testid="module-generator-page">
                {/* Header */}
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-900 via-primary/20 to-slate-900 p-8 border border-white/10">
                    <div className="absolute inset-0 bg-grid-white/5" />
                    <div className="relative">
                        <div className="flex items-center gap-4 mb-4">
                            <div className="p-3 rounded-xl bg-gradient-to-br from-primary to-secondary">
                                <Brain className="h-8 w-8 text-white" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
                                    AI Module Generator
                                    <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white border-0">
                                        <Sparkles className="h-3 w-3 mr-1" />
                                        Futuristic
                                    </Badge>
                                </h1>
                                <p className="text-muted-foreground mt-1">
                                    Master any topic in the universe. Generate complete training from a single prompt.
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-4">
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Globe className="h-3 w-3" /> Any Subject
                            </span>
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Atom className="h-3 w-3" /> Quantum to Finance
                            </span>
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Infinity className="h-3 w-3" /> Limitless Knowledge
                            </span>
                        </div>
                    </div>
                </div>

                <div className="grid gap-6 lg:grid-cols-3">
                    {/* Main Generator Panel */}
                    <div className="lg:col-span-2">
                        <Tabs value={activeTab} onValueChange={setActiveTab}>
                            <TabsList className="grid w-full grid-cols-2">
                                <TabsTrigger value="quick" className="gap-2">
                                    <Zap className="h-4 w-4" />
                                    Quick Generate
                                </TabsTrigger>
                                <TabsTrigger value="advanced" className="gap-2">
                                    <Wand2 className="h-4 w-4" />
                                    Advanced
                                </TabsTrigger>
                            </TabsList>

                            {/* Quick Generate */}
                            <TabsContent value="quick">
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <Rocket className="h-5 w-5 text-primary" />
                                            Quick Module Generation
                                        </CardTitle>
                                        <CardDescription>
                                            Just tell me what you want to learn. I'll create everything.
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="relative">
                                            <Textarea
                                                value={quickPrompt}
                                                onChange={(e) => setQuickPrompt(e.target.value)}
                                                placeholder="Examples:
• Generate training modules for stock trading
• Create an HR advanced certification course  
• Build quantum computing fundamentals
• Develop leadership skills for managers
• Teach me about parallel universes and multiverse theory"
                                                rows={5}
                                                className="resize-none"
                                                data-testid="quick-prompt"
                                            />
                                        </div>

                                        {/* Example Topics */}
                                        <div className="space-y-2">
                                            <Label className="text-xs text-muted-foreground">Quick Select:</Label>
                                            <div className="flex flex-wrap gap-2">
                                                {exampleTopics.slice(0, 4).map((topic, i) => (
                                                    <Button
                                                        key={i}
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => selectExampleTopic(topic)}
                                                        className="text-xs"
                                                    >
                                                        {topic.icon} {topic.name}
                                                    </Button>
                                                ))}
                                            </div>
                                        </div>

                                        <Button
                                            onClick={handleQuickGenerate}
                                            disabled={loading || !quickPrompt.trim()}
                                            className="w-full h-12 text-lg"
                                            data-testid="generate-btn"
                                        >
                                            {loading ? (
                                                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                                            ) : (
                                                <Sparkles className="h-5 w-5 mr-2" />
                                            )}
                                            Generate Complete Module
                                        </Button>
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            {/* Advanced Generate */}
                            <TabsContent value="advanced">
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <Wand2 className="h-5 w-5 text-secondary" />
                                            Advanced Configuration
                                        </CardTitle>
                                        <CardDescription>
                                            Fine-tune your module generation with detailed settings
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="grid gap-4 md:grid-cols-2">
                                            <div className="space-y-2 md:col-span-2">
                                                <Label>Topic / Subject *</Label>
                                                <Input
                                                    value={advancedForm.topic}
                                                    onChange={(e) => setAdvancedForm(prev => ({ ...prev, topic: e.target.value }))}
                                                    placeholder="e.g., Quantum Computing, Advanced Excel, HR Management"
                                                    data-testid="adv-topic"
                                                />
                                            </div>

                                            <div className="space-y-2 md:col-span-2">
                                                <Label>Description (Optional)</Label>
                                                <Textarea
                                                    value={advancedForm.description}
                                                    onChange={(e) => setAdvancedForm(prev => ({ ...prev, description: e.target.value }))}
                                                    placeholder="Additional context or specific focus areas..."
                                                    rows={2}
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <Label>Difficulty Level</Label>
                                                <Select 
                                                    value={advancedForm.difficulty}
                                                    onValueChange={(v) => setAdvancedForm(prev => ({ ...prev, difficulty: v }))}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="beginner">Beginner</SelectItem>
                                                        <SelectItem value="intermediate">Intermediate</SelectItem>
                                                        <SelectItem value="advanced">Advanced</SelectItem>
                                                        <SelectItem value="expert">Expert</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>

                                            <div className="space-y-2">
                                                <Label>Target Audience</Label>
                                                <Select
                                                    value={advancedForm.target_audience}
                                                    onValueChange={(v) => setAdvancedForm(prev => ({ ...prev, target_audience: v }))}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="students">Students</SelectItem>
                                                        <SelectItem value="professionals">Professionals</SelectItem>
                                                        <SelectItem value="executives">Executives</SelectItem>
                                                        <SelectItem value="researchers">Researchers</SelectItem>
                                                        <SelectItem value="general">General Public</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>

                                            <div className="space-y-2">
                                                <Label>Number of Lessons</Label>
                                                <Select
                                                    value={String(advancedForm.num_lessons)}
                                                    onValueChange={(v) => setAdvancedForm(prev => ({ ...prev, num_lessons: Number(v) }))}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {[3, 5, 7, 10, 15].map(n => (
                                                            <SelectItem key={n} value={String(n)}>{n} Lessons</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>

                                            <div className="space-y-2">
                                                <Label>Language</Label>
                                                <Select
                                                    value={advancedForm.language}
                                                    onValueChange={(v) => setAdvancedForm(prev => ({ ...prev, language: v }))}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="English">English</SelectItem>
                                                        <SelectItem value="Spanish">Spanish</SelectItem>
                                                        <SelectItem value="French">French</SelectItem>
                                                        <SelectItem value="German">German</SelectItem>
                                                        <SelectItem value="Chinese">Chinese</SelectItem>
                                                        <SelectItem value="Japanese">Japanese</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>

                                        {/* Include Options */}
                                        <div className="space-y-4 pt-4 border-t">
                                            <Label className="text-sm font-medium">Include in Module:</Label>
                                            <div className="grid gap-4 md:grid-cols-3">
                                                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                                                    <div className="flex items-center gap-2">
                                                        <ClipboardCheck className="h-4 w-4 text-primary" />
                                                        <span className="text-sm">Assessments</span>
                                                    </div>
                                                    <Switch
                                                        checked={advancedForm.include_assessments}
                                                        onCheckedChange={(v) => setAdvancedForm(prev => ({ ...prev, include_assessments: v }))}
                                                    />
                                                </div>
                                                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                                                    <div className="flex items-center gap-2">
                                                        <Users className="h-4 w-4 text-secondary" />
                                                        <span className="text-sm">Simulations</span>
                                                    </div>
                                                    <Switch
                                                        checked={advancedForm.include_simulations}
                                                        onCheckedChange={(v) => setAdvancedForm(prev => ({ ...prev, include_simulations: v }))}
                                                    />
                                                </div>
                                                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                                                    <div className="flex items-center gap-2">
                                                        <Presentation className="h-4 w-4 text-emerald-500" />
                                                        <span className="text-sm">Visuals</span>
                                                    </div>
                                                    <Switch
                                                        checked={advancedForm.include_visuals}
                                                        onCheckedChange={(v) => setAdvancedForm(prev => ({ ...prev, include_visuals: v }))}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <Button
                                            onClick={handleAdvancedGenerate}
                                            disabled={loading || !advancedForm.topic.trim()}
                                            className="w-full h-12 text-lg"
                                        >
                                            {loading ? (
                                                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                                            ) : (
                                                <Wand2 className="h-5 w-5 mr-2" />
                                            )}
                                            Generate Custom Module
                                        </Button>
                                    </CardContent>
                                </Card>
                            </TabsContent>
                        </Tabs>

                        {/* Generation Progress */}
                        {currentGeneration && currentGeneration.status === 'processing' && (
                            <Card className="mt-6 border-primary/50 bg-primary/5">
                                <CardHeader className="pb-2">
                                    <CardTitle className="flex items-center gap-2 text-lg">
                                        <Loader2 className="h-5 w-5 animate-spin text-primary" />
                                        Generating Module...
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-3">
                                        <Progress value={currentGeneration.progress || 0} className="h-3" />
                                        <p className="text-sm text-muted-foreground">
                                            {currentGeneration.status_message || 'Initializing...'}
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {[
                                                { label: 'Analyzing', threshold: 10 },
                                                { label: 'Creating Lessons', threshold: 30 },
                                                { label: 'Assessments', threshold: 60 },
                                                { label: 'Simulations', threshold: 75 },
                                                { label: 'Documents', threshold: 85 },
                                                { label: 'Finalizing', threshold: 95 }
                                            ].map((step, i) => (
                                                <Badge 
                                                    key={i}
                                                    variant={(currentGeneration.progress || 0) >= step.threshold ? 'default' : 'outline'}
                                                    className="text-xs"
                                                >
                                                    {(currentGeneration.progress || 0) >= step.threshold && (
                                                        <CheckCircle className="h-3 w-3 mr-1" />
                                                    )}
                                                    {step.label}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-4">
                        {/* What You Get */}
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Sparkles className="h-5 w-5 text-yellow-500" />
                                    What You Get
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {[
                                    { icon: BookOpen, label: 'Complete Lessons', desc: 'Detailed content for each topic' },
                                    { icon: ClipboardCheck, label: 'Assessments', desc: 'MCQs, scenarios, exercises' },
                                    { icon: MessageSquare, label: 'AI Simulations', desc: 'Interactive role-play scenarios' },
                                    { icon: FileText, label: 'Reference Docs', desc: 'Comprehensive study materials' },
                                    { icon: Presentation, label: 'Presentations', desc: 'Ready-to-use slide decks' },
                                ].map((item, i) => (
                                    <div key={i} className="flex items-start gap-3 p-2 rounded-lg hover:bg-muted/50">
                                        <item.icon className="h-5 w-5 text-primary mt-0.5" />
                                        <div>
                                            <p className="text-sm font-medium">{item.label}</p>
                                            <p className="text-xs text-muted-foreground">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>

                        {/* Recent Generations */}
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Clock className="h-5 w-5" />
                                    Recent Generations
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                <ScrollArea className="h-[250px]">
                                    {generations.length === 0 ? (
                                        <div className="p-6 text-center text-muted-foreground">
                                            <GraduationCap className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                            <p className="text-sm">No modules generated yet</p>
                                        </div>
                                    ) : (
                                        <div className="divide-y">
                                            {generations.map((gen, i) => (
                                                <div key={i} className="p-3 hover:bg-muted/50">
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-medium truncate">{gen.topic}</p>
                                                            <p className="text-xs text-muted-foreground">
                                                                {gen.num_lessons} lessons • {gen.difficulty}
                                                            </p>
                                                        </div>
                                                        <Badge 
                                                            variant={gen.status === 'completed' ? 'default' : gen.status === 'processing' ? 'secondary' : 'destructive'}
                                                            className="text-xs ml-2"
                                                        >
                                                            {gen.status === 'completed' && <CheckCircle className="h-3 w-3 mr-1" />}
                                                            {gen.status === 'processing' && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                                                            {gen.status}
                                                        </Badge>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </ScrollArea>
                            </CardContent>
                        </Card>

                        {/* Topic Ideas */}
                        <Card className="bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium">Explore Any Topic</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="flex flex-wrap gap-1.5">
                                    {exampleTopics.map((topic, i) => (
                                        <button
                                            key={i}
                                            onClick={() => selectExampleTopic(topic)}
                                            className="px-2 py-1 text-xs rounded-full bg-background/50 hover:bg-background border border-border/50 hover:border-primary/50 transition-colors"
                                        >
                                            {topic.icon} {topic.category}
                                        </button>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default ModuleGenerator;
